import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { Account, AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { DeviationEvent, EventsDataService } from '@services/events-data.service';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetDistribution, ModelEdit, ModelsService, ModelType } from '@services/models.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { Location } from '@angular/common';
import { convertEpochToDateString } from '@functions/functions';
import { CreateAssetDistributionComponent } from '@dialogs/create-asset-distribution/create-asset-distribution.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** foundStatus */
export type foundStatus = '' | 'Found' | 'notFound';

/**
 * ModelDetailsComponent
 */
@Component({
	selector: 'app-model-details',
	templateUrl: './model-details.component.html',
	styleUrls: ['./model-details.component.scss']
})
export class ModelDetailsComponent implements OnInit {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** asset */
	public asset: LevelAsset;

	/** deviationFields */
	public deviationFields: Object = { text: 'Message', id: 'DeviationEventID' };

	/** editFields */
	public editFields: Object = { text: 'PreviousProperties', id: 'ModelEditID' };

	/** accountID */
	public accountID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** OrganisationID */
	public OrganisationUID: string;

	/** currentUser */
	public currentUser: WebAppUser;

	/** modelType */
	public modelTypeID: number;

	/** modelID */
	public modelID: number;

	/** model */
	public model: AssetDistribution | any;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[] = [];

	/** assetList */
	public assetList: LevelAsset[] = [];

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** modelType */
	public selectedModelType: ModelType | undefined;

	/** modelEdits */
	public modelEdits: ModelEdit[] = [];

	/** deviationEvents */
	public deviationEvents: DeviationEvent[] = [];

	/** filteredDeviationEvents */
	public filteredDeviationEvents: DeviationEvent[] = [];

	/** modelFoundStatus */
	public modelFoundStatus: foundStatus = '';

	/** CMMSCase */
	public CMMSCase: string | undefined = undefined;

	/** AccountFeatures */
	AccountFeatures = AccountFeatures;

	/**
	 * Default Constructor
	 * @param eventsDataService eventsDataService  
	 * @param assetsDataService assetsDataService 
	 * @param modelsService modelsService 
	 * @param dialog dialog 
	 * @param authenticationService authenticationService  
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 * @param route route 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public eventsDataService: EventsDataService,
		public assetsDataService: AssetsDataService,
		public modelsService: ModelsService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private location: Location
	) { }

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {

		this.globalFunctions.displaySpinner(true);
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.paramMap.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, account, currentUser, params, queryParams, hierarchy]) =>
				!!selectedAccountGUID && !!selectedOrganisationUID && !!account && !!currentUser && !!params && !!queryParams && !!hierarchy),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([selectedAccountGUID, account, selectedOrganisationUID, currentUser, params, queryParams, hierarchy]) => {

				this.accountID = selectedAccountGUID;
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.hierarchy = JSON.parse(hierarchy);

				this.assetList = this.flattenAssets(this.hierarchy);

				this.modelID = parseInt(params.get('modelID') ?? '0');
				this.modelTypeID = parseInt(queryParams.get('modelType') ?? '0');

				this.selectedModelType = await this.getModelTypes(this.modelTypeID);
				await this.getModel(this.modelTypeID, this.modelID);

				this.CMMSCase = this.model.CaseID ? this.model.CaseID : undefined

				this.modelFoundStatus = this.model ? 'Found' : 'notFound'
				this.globalFunctions.displaySpinner(false);

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				this.globalFunctions.displaySpinner(false);
			}
		});

		this.siteSubscriptions.refreshModelEdits$.pipe(
			distinctUntilChanged(),
			filter((refresh) => !!refresh),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async () => {
				if (this.selectedModelType && this.model) {
					await this.getModelEdits(this.selectedModelType.ModelTypeID, this.model.AssetDistributionID);
					this.siteSubscriptions.RefreshModelEdits(false);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in updateModelList$", false);
			}
		});
	}

	/**
	 * getModel
	 */
	async getModel(modelTypeID: number, modelID: number) {

		if (modelTypeID == 1) {
			try {
				const distribution = await firstValueFrom(
					this.modelsService.getAssetDistribution$(this.accountID, this.OrganisationUID, 0, modelID)
				);

				if (distribution) {
					const _model = <AssetDistribution>distribution[0];

					if (_model) {
						this.model = _model

						let asset = this.assetList.find((x: LevelAsset) => x.AssetID == this.model.AssetID);
						if (asset) {
							this.asset = asset;
						}

						this.getDeviationEvents(modelTypeID, this.model.AssetDistributionID);
						this.getModelEdits(modelTypeID, this.model.AssetDistributionID);

					}
				}
			}
			catch (error: any) {
				this.model = undefined;
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: this.modelsService.getAssetDistribution$", false); }
			} finally {
				this.globalFunctions.displaySpinner(false)
			}

		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * getModelTypes
	 */
	async getModelTypes(modelTypeID: number): Promise<ModelType | undefined> {

		try {
			const modelTypes = await firstValueFrom(
				this.modelsService.getModelTypes$(this.accountID, this.OrganisationUID)
			);

			if (modelTypes) {
				this.modelTypes = modelTypes;
				let _type = this.modelTypes.find((x: ModelType) => x.ModelTypeID == modelTypeID);
				if (_type) { return _type; }
			}
			return undefined
		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.modelsService.getModelTypes$", false); }
			return undefined
		} finally { }

	}

	/** back */
	back() {
		this.location.back();
	}

	/**
	 * getDate
	 * @param timestamp 
	 * @returns 
	 */
	getDate(timestamp: number): string {
		return convertEpochToDateString(timestamp)
	}

	/**
	 * goToAsset
	 */
	goToAsset() {
		this.siteSubscriptions.SetSelectedAsset(this.asset);
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/location/${this.asset.ParentUID}/asset/${this.asset.AssetID}`]);
	}

	/**
	 * goToModels
	 */
	goToModels() {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/models`])
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
		}
		return false;
	}

	/**
	 * viewCMMS
	 */
	viewCMMS() {
		this.router.navigate([
			this.route.snapshot.paramMap.get('account'),
			'organisation',
			this.route.snapshot.paramMap.get('organisation'),
			'SAPCases',
			this.CMMSCase
		]);
	}

	/**
	 * viewModel
	 */
	async viewModel() {
		if (this.selectedModelType && this.model) {
			if (this.selectedModelType.ModelTypeID == 1) {
				let data = {
					asset: this.asset,
					viewing: true,
					distributionID: this.model.AssetDistributionID,
					existingDistribution: this.model
				}

				const dialogRef = this.dialog.open(CreateAssetDistributionComponent
					, { data: data }
				);

				dialogRef.afterClosed().pipe(
					filter(result => !!result)
				).subscribe(result => {
				});
			}
		} else {
			let message = [
				`This model has been deleted since this event was created.`
			].filter(s => !!s).join('\n');

			/** Do Nothing */
			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$(
					message,
					"View Model",
					undefined,
					null
				)
			);
		}
	}

	/**
	 * getModelEdits
	 */
	async getModelEdits(ModelTypeID: number, AssetDistributionID: any): Promise<boolean> {

		try {
			const edits = await firstValueFrom(
				this.modelsService.getModelEdits$(this.accountID, this.OrganisationUID, ModelTypeID, AssetDistributionID)
			);

			if (edits) {
				this.modelEdits = edits;
			}
			return true;
		}
		catch (error: any) {
			this.modelEdits = [];
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.modelsService.getModelEdits$", false); }

			return false

		} finally { }

	}

	/** 
	 * getDeviationEvents
	 */
	async getDeviationEvents(ModelTypeID: number, AssetDistributionID: any) {

		try {
			if (ModelTypeID == 1) {
				const events = await firstValueFrom(
					this.eventsDataService.getDeviationEvents$(this.accountID, this.OrganisationUID, 0, 0, 0, 0,
						ModelTypeID, AssetDistributionID)
				)

				if (events) {
					this.deviationEvents = events;
					this.filteredDeviationEvents = this.deviationEvents;
				}
			}
		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getDeviationEvents$", false); }
		} finally { }

	}

	/**
	 * goToEvent
	 */
	goToEvent(event: DeviationEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${event.DeviationEventID}`], { queryParams: { eventType: 4 } });
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFiltering(event: any): void {
		event.cancel = true;
		const query = event.text.toLowerCase();
		this.filteredDeviationEvents = this.deviationEvents.filter((data: DeviationEvent) => {
			const dateStr = `${convertEpochToDateString(data.Timestamp)}`.toLowerCase();
			const assetInfo = `${data.AssetName + ' - ' + data.AssetDescription || ''}`.toLowerCase();
			const eventID = data.DeviationEventID.toString().toLowerCase();
			const tolerance = data.AcceptableDeviation.toString().toLowerCase();
			const sensitivity = data.Sensitivity.toString().toLowerCase();
			const percentage = data.PercentageDeviated.toString().toLowerCase();

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${assetInfo} ${eventID} ${tolerance} ${sensitivity} ${percentage}`;
			return combinedStr.includes(query);
		});
	}

	/**
	 * patchOldDistribution 
	 * @param distribution distribution 
	 * @param active active 
	 * @param disabled disabled 
	 */
	async patchOldDistribution(distribution: AssetDistribution, active: boolean, disabled: boolean) {

		if (disabled) {
			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$('Are you sure you want to delete this model?')
			);

			if (!OKResult) { return; }
		}
		else {
			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$(`Are you sure you want to ${active ? 'Activate' : 'De Activate'} this model?`)
			);

			if (!OKResult) { return; }
		}

		try {
			const patchResult = await firstValueFrom(
				this.modelsService.patchOldDistribution$(this.accountID, this.OrganisationUID,
					distribution.AssetDistributionID, this.asset.AssetID, active, disabled)
			);

			if (patchResult) {
				if (patchResult[0].Result.includes("OK")) {
					this.globalFunctions.DisplaySnackBar({
						message: active ? 'Model activated' : (!active && !disabled) ? 'Model deactivated' : disabled ? 'Model deleted' : '',
						type: SnackType.Info,
						duration: 1000
					});
					if (disabled) {
						this.goToModels();
						return
					} else {
						await this.getModel(this.modelTypeID, this.model.AssetDistributionID);
					}
				}
			}

		}
		catch (error) {
			this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false);
		}

	}
}
